<template lang="pug">
.family-message-history-detail-message-register-detail-accordion
  b-card-header#reply-list.p-2(header-tag="header" role="tab" header-class="accordion-toggle" v-b-toggle.accordion-reply-list)
    span.toggle-text 返信リスト（未読：{{ unreadCount }}件）
  b-collapse#accordion-reply-list(accordion="reply-list-accordion" role="tabpanel")
    b-card-body.p-2
      span(v-show="!isBusy && replyItems.length < 1") 返信はありません。
      MessageHistoryReplyList(v-model="replyItems" :isBusy="isBusy" @onMessage="onReplyMessage" @onReadSwitch="onReadSwitch")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { userStore } from "@/store/store";
import { MobileMessageHistoryState } from "@/store/MobileMessageHistory";

import SendMessageRepliesService, { SendMessageReplyModel, SendMessageReplySenderType } from "@/commons/services/SendMessageReplies";

import MessageHistoryReplyList, { MessageHistoryReplyListItem, MessageHistoryReplyListType } from "@/mobile/components/lists/MessageHistoryReplyList.vue";

/**
 * ### 送信履歴詳細(家族)返信リストアコーディオン
 */
@Component({
  components: {
    MessageHistoryReplyList
  }
})
export default class MessageReplyListAccordion extends Vue {
  /** メッセージ送信の返信サービス */
  @Inject("SendMessageRepliesService") sendMessageRepliesService!: SendMessageRepliesService;

  /** メッセージ一覧で選択したメッセージ情報 */
  @Getter("mobileMessageHistoryState") state!: MobileMessageHistoryState;

  /** ローディングの表示可否 */
  isBusy: boolean = false;
  /** 返信未読数 */
  unreadCount: number = 0;
  /** 返信メッセージ */
  replyItems: Array<MessageHistoryReplyListItem> = [];

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   *
   * - メッセージの返信を表示するコンポーネント用の情報を作成する
   * - 画面表示用に返信未読数をカウントする
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // メッセージの返信を表示するコンポーネント用の情報を作成する
    const replyMessages: Array<SendMessageReplyModel> = await this.getReplyMessages();

    /**
     * 個別メッセージと共有メッセージで表示を切替える
     *
     * 個別メッセージ：返信を家族毎にまとめてリスト表示
     * 共有メッセージ：1行「メッセージを表示」とだけ表示
     */
    // 個別メッセージ
    if (this.state.isSharedMessages == null || this.state.isSharedMessages == false) {
      const replyItems: Array<MessageHistoryReplyListItem> = this.createReplyItems(replyMessages);
      this.replyItems = replyItems;

      // 画面表示用に返信未読数をカウントする
      let unreadCount = 0;
      for (const message of replyItems) {
        if (message.readed == false) {
          unreadCount++;
        }
      }
      this.unreadCount = unreadCount;
    }
    // 共有メッセージ
    else {
      // 行情報と未読数を作成する
      const { item, unreadCount } = this.createSharedReplyItem(replyMessages);
      this.replyItems = [item];
      this.unreadCount = unreadCount;
    }

    this.isBusy = false;

    return;
  }

  /**
   * ### メッセージに対する返信を取得する
   */
  public async getReplyMessages(): Promise<Array<SendMessageReplyModel>> {
    const replyMessages: Array<SendMessageReplyModel> = await this.sendMessageRepliesService.get(userStore, Number(this.state.datetime));
    return replyMessages;
  }

  /**
   * ### 個別メッセージの返信リストの行情報を作成する
   */
  public createReplyItems(replies: Array<SendMessageReplyModel>): Array<MessageHistoryReplyListItem> {
    // 返信メッセージの1行はユーザーの1番古いデータになるので、datetimeの古い順にソートする
    const sortedReplies: Array<SendMessageReplyModel> = replies.sort((a, b) => a.datetime - b.datetime);

    // ユーザー単位でメッセージをMAP化する
    let map: Map<string, Array<SendMessageReplyModel>> = new Map<string, Array<SendMessageReplyModel>>();
    for (const reply of sortedReplies) {
      const messages: Array<SendMessageReplyModel> | undefined = map.get(`${reply.residentId}_${reply.familyId}`);
      if (messages) {
        messages.push(reply);
        map.set(`${reply.residentId}_${reply.familyId}`, messages);
      } else {
        map.set(`${reply.residentId}_${reply.familyId}`, [reply]);
      }
    }

    // MAP化したものを行情報にする
    let items: Array<MessageHistoryReplyListItem> = [];
    map.forEach((value: Array<SendMessageReplyModel>) => {
      const sortedValue: Array<SendMessageReplyModel> = value.sort((a, b) => a.datetime - b.datetime);

      // 行の並び順は最後のメッセージの日付の新しいもの順にしたいので、行情報は最新のものを使用する
      const index: number = sortedValue.length - 1;

      // 未読かどうかは家族の返信で未読が存在するか否かで判断する
      let readed: boolean = true;
      for (const value of sortedValue) {
        if (value.senderType == SendMessageReplySenderType.FAMILY && value.readUserIds.indexOf(userStore.loginUser.userId) < 0) {
          readed = false;
          break;
        }
      }

      items.push({
        parentMessagesSortKey: sortedValue[index].parentMessagesSortKey,
        residentId: sortedValue[index].residentId,
        familyId: sortedValue[index].familyId,

        isSharedMessages: this.state.isSharedMessages,
        receiverDetails: this.state.receiverDetails,

        mainName: sortedValue[index].familyName,
        subTitle: `${sortedValue[index].residentName}・${sortedValue[index].familyRelation}`,
        subName: `${sortedValue[index].residentRoom}・${sortedValue[index].residentName}・${sortedValue[index].familyRelation}`,
        datetime: sortedValue[index].datetime,
        readed: readed,
        type: sortedValue[index].type as MessageHistoryReplyListType,
        messages: value
      });
    });

    // 画面表示では日付の新しい順なのでソートしなおす
    const sortedItems: Array<MessageHistoryReplyListItem> = items.concat().sort((a, b) => (b.datetime as number) - (a.datetime as number));

    /**
     * 共有メッセージの場合、施設が家族に返信したものはresidentId、familyId共に0で登録している
     * このリストに表示してはいけないので、フィルタリングして施設からの返信は除く
     */
    const filteredItems: Array<MessageHistoryReplyListItem> = sortedItems.filter((item) => {
      return item.residentId != 0 && item.familyId != 0 ? true : false;
    });

    return filteredItems;
  }

  /**
   * ### 共有メッセージの行情報を作成する
   */
  createSharedReplyItem(replies: Array<SendMessageReplyModel>): { item: MessageHistoryReplyListItem; unreadCount: number } {
    // 返信メッセージの1行はユーザーの1番古いデータになるので、datetimeの古い順にソートする
    const sortedReplies: Array<SendMessageReplyModel> = replies.sort((a, b) => a.datetime - b.datetime);
    // 末尾のものが最後のメッセージなので最終受信日を取得する
    const latestDatetime = 0 < sortedReplies.length ? sortedReplies[sortedReplies.length - 1].datetime : null;

    // メッセージが全て確認済みかどうか確認する
    // また、画面表示用に返信未読数をカウントする
    let unreadCount = 0;
    let sharedReaded: boolean = true;
    for (const message of replies) {
      if (message.senderType == SendMessageReplySenderType.FAMILY) {
        // メッセージが全て確認済みかどうか
        if (sharedReaded == true && !message.readUserIds.includes(userStore.loginUser.userId)) {
          sharedReaded = false;
        }
        // 未読数をカウントする
        if (!message.readUserIds.includes(userStore.loginUser.userId)) {
          unreadCount++;
        }
      }
    }

    return {
      item: {
        parentMessagesSortKey: `0_0_${this.state.datetime}`,
        residentId: 0,
        familyId: 0,

        isSharedMessages: this.state.isSharedMessages,
        receiverDetails: this.state.receiverDetails,

        mainName: "",
        subTitle: "共有メッセージを表示",
        subName: "",
        datetime: latestDatetime,
        readed: sharedReaded,
        type: MessageHistoryReplyListType.REPLY,
        messages: sortedReplies
      },
      unreadCount
    };
  }

  /**
   * ### 返信の未読押下イベント
   *
   * - 画面表示用の返信未読数を更新するためデクリメントする
   * - 親コンポーネントに返信の未読が押下されたことを通知する
   */
  public onReplyMessage(message: MessageHistoryReplyListItem): void {
    if (message.readed == false) {
      this.unreadCount = this.unreadCount - 1;
    }
    this.$emit("onReplyMessage", message);
    return;
  }

  /**
   * ### 未読／既読のスイッチ押下イベント
   *
   * - 親コンポーネントに押下されたことを通知する
   */
  public onReadSwitch(message: MessageHistoryReplyListItem): void {
    this.$emit("onReadSwitch", message);
    return;
  }
}
</script>
